<template>
  <div data-testid="fechamentos-modal">
    <modal
      elem-id="modal_crud_fechamento"
      :size="modalSize"
      :close-label="t('Fechar')"
      :save-label="saveLabel"
      :save-fn="handleSave"
      :save-disabled-fn="saveDisabledFn"
      :title="modalTitle"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
    >
      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isVisible">
        <!-- Create / Edit Mode - Show form -->
        <div v-if="isFormMode" data-testid="fechamentos-modal-form">
          <fechamentos-form
            ref="formRef"
            :fechamento="currentFechamento"
            :mode="(currentMode as 'create' | 'edit')"
            @saved="(result: unknown) => handleSaveSuccess(result as FechamentoData)"
            @cancel="closeModal"
          />
        </div>

        <!-- View Mode - Show details -->
        <div v-else-if="currentMode === 'view'" data-testid="fechamentos-modal-view">
          <fechamentos-detalhes
            :fechamento="currentFechamento"
            @edit="handleEditFromView"
            @close="closeModal"
          />
        </div>

        <!-- Reopen Mode - Show motivo form -->
        <div v-else-if="currentMode === 'reopen'" data-testid="fechamentos-modal-reopen">
          <div class="py-3">
            <p class="text-muted mb-3">
              {{ t('Informe o motivo para reabrir o período') }} {{ currentFechamento?.periodo_formatado }}
            </p>
            <div class="mb-3">
              <input-text
                v-model="reopenMotivo"
                elem-id="fechamento_reopen_motivo"
                :label="t('Motivo da reabertura')"
                :placeholder="t('Informe o motivo da reabertura')"
                :multiline="true"
                :rows="4"
                class="w-100"
                data-testid="fechamentos-modal-reopen-motivo"
              />
            </div>
          </div>
        </div>

        <!-- Close Mode - Show confirmation with resumo -->
        <div v-else-if="currentMode === 'close'" data-testid="fechamentos-modal-close">
          <div class="text-center py-3">
            <i class="pi pi-lock text-warning" style="font-size: 3rem;"></i>
            <h5 class="mt-3">{{ t('Confirmar fechamento do periodo') }}</h5>
            <p class="text-muted">{{ t('Esta acao nao podera ser desfeita sem motivo de reabertura') }}</p>
          </div>
          <div class="close-summary" data-testid="fechamentos-modal-close-summary">
            <div class="summary-row">
              <span class="summary-label">{{ t('Período') }}</span>
              <span class="summary-value">{{ currentFechamento?.periodo_formatado }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">{{ t('Meta de faturamento') }}</span>
              <span class="summary-value">{{ n(Number(currentFechamento?.meta_faturamento) || 0, 'currency') }}</span>
            </div>
            <template v-if="closeResumo">
              <div class="summary-row">
                <span class="summary-label">{{ t('Total realizado') }}</span>
                <span class="summary-value">{{ n(Number(closeResumo.total_efetivo) || 0, 'currency') }}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">{{ t('Execucao') }}</span>
                <span class="summary-value" :class="(closeResumo.variacao_percentual ?? 0) >= 100 ? 'text-success' : (closeResumo.variacao_percentual ?? 0) >= 80 ? 'text-warning' : 'text-danger'">
                  {{ closeResumo.variacao_percentual || 0 }}%
                </span>
              </div>
              <div class="summary-row">
                <span class="summary-label">{{ t('Total despesas') }}</span>
                <span class="summary-value">{{ n(Number(closeResumo.total_despesas) || 0, 'currency') }}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">{{ t('Lucro liquido') }}</span>
                <span class="summary-value" :class="(Number(closeResumo.lucro_liquido) || 0) >= 0 ? 'text-success' : 'text-danger'">
                  {{ n(Number(closeResumo.lucro_liquido) || 0, 'currency') }}
                </span>
              </div>
            </template>
            <div v-else-if="isLoading" class="text-center py-2">
              <i class="pi pi-spin pi-spinner"></i>
            </div>
          </div>
        </div>

        <!-- Delete Mode - Show confirmation -->
        <div v-else-if="currentMode === 'delete'" data-testid="fechamentos-modal-delete">
          <div class="text-center py-4">
            <i class="pi pi-exclamation-triangle text-warning" style="font-size: 3rem;"></i>
            <h5 class="mt-3">{{ t('Confirmar exclusao') }}</h5>
            <p class="text-muted">
              {{ t('Deseja excluir o fechamento do periodo') }} {{ currentFechamento?.periodo_formatado }}?
            </p>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFechamentos } from '../../composables/useFechamentos';

import FechamentosForm from './FechamentosForm.vue';
import FechamentosDetalhes from './FechamentosDetalhes.vue';
import type { Fechamentos_ShowResponseDados } from '../../contracts/Fechamentos_ShowResponse';
import type { Fechamentos_ResumoResponseDados } from '../../contracts/Fechamentos_ResumoResponse';

type ModalMode = 'create' | 'edit' | 'view' | 'delete' | 'reopen' | 'close';

/**
 * Single-fechamento detail row used by the modal. Backed by the
 * Fechamentos_ShowResponseDados contract.
 */
type FechamentoData = Fechamentos_ShowResponseDados;

interface ModalOpenPayload {
  id?: number | string;
  mode?: ModalMode;
  fechamento?: FechamentoData;
}

interface FechamentosFormInstance {
  canSave: boolean;
  hasOpenFechamento: boolean;
  saveFechamento: () => Promise<void>;
}

// Composables
const { t, n } = useI18n();
const { fetchFechamento, deleteFechamento, reabrirFechamento, fecharFechamento, fetchResumo } = useFechamentos();

// Reactive data
const isVisible = ref<boolean>(false);
const currentMode = ref<ModalMode>('create');
const currentFechamento = ref<FechamentoData | null>(null);
const currentId = ref<number | string | null>(null);
const formRef = ref<FechamentosFormInstance | null>(null);
const isLoading = ref<boolean>(false);
const isDeleting = ref<boolean>(false);
const reopenMotivo = ref<string>('');
const closeResumo = ref<Fechamentos_ResumoResponseDados | null>(null);

// Computed properties
const isFormMode = computed<boolean>(() => {
  return currentMode.value === 'create' || currentMode.value === 'edit';
});

const modalTitle = computed<string>(() => {
  log('FechamentosModal', 'modalTitle', 'Computing title', { mode: currentMode.value });
  switch (currentMode.value) {
    case 'create':
      return t('Novo fechamento');
    case 'edit':
      return t('Editar fechamento');
    case 'view':
      return t('Detalhes do fechamento');
    case 'delete':
      return t('Excluir fechamento');
    case 'reopen':
      return t('Reabrir período');
    case 'close':
      return t('Fechar período');
    default:
      return '';
  }
});

const modalSize = computed<string>(() => {
  if (currentMode.value === 'view') {
    return 'large';
  }
  if (currentMode.value === 'delete') {
    return 'small';
  }
  return 'modalMd';
});

const saveLabel = computed<string | undefined>(() => {
  if (currentMode.value === 'create') {
    return t('Criar');
  }
  if (currentMode.value === 'edit') {
    return t('Salvar');
  }
  if (currentMode.value === 'delete') {
    return t('Excluir');
  }
  if (currentMode.value === 'reopen') {
    return t('Confirmar');
  }
  if (currentMode.value === 'close') {
    return t('Confirmar Fechamento');
  }
  // For view, no save button needed
  return undefined;
});

const saveDisabledFn = computed<(() => boolean) | undefined>(() => {
  if (currentMode.value === 'create' || currentMode.value === 'edit') {
    return () => !formRef.value?.canSave || !!formRef.value?.hasOpenFechamento;
  }
  if (currentMode.value === 'delete') {
    return () => isDeleting.value;
  }
  if (currentMode.value === 'reopen') {
    return () => !reopenMotivo.value.trim() || isDeleting.value;
  }
  if (currentMode.value === 'close') {
    return () => isDeleting.value;
  }
  return undefined;
});

// Methods
const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('FechamentosModal', 'handleModalOpen', 'Modal open event received', data);

  const mode: ModalMode = data?.mode || (data?.id ? 'edit' : 'create');
  currentMode.value = mode;
  currentId.value = data?.id || null;
  currentFechamento.value = null;

  // Pre-load fechamento from event data if provided (avoids extra API call)
  if (data?.fechamento) {
    currentFechamento.value = data.fechamento;
  }

  isVisible.value = true;

  log('FechamentosModal', 'handleModalOpen', 'Modal opened', {
    mode: currentMode.value,
    id: currentId.value
  });

  // Load fechamento data for edit/view/delete/close modes
  if (mode !== 'create' && currentId.value && !data?.fechamento) {
    await loadFechamento(currentId.value);
  }

  // Load resumo for close mode
  if (mode === 'close' && currentId.value) {
    try {
      closeResumo.value = await fetchResumo(currentId.value) as Fechamentos_ResumoResponseDados;
    } catch (err) {
      log('FechamentosModal', 'handleModalOpen', 'Error loading resumo for close mode', err);
    }
  }
};

const loadFechamento = async (id: number | string): Promise<void> => {
  log('FechamentosModal', 'loadFechamento', 'Loading fechamento', { id });
  isLoading.value = true;

  try {
    const result = await fetchFechamento(id) as FechamentoData;
    currentFechamento.value = result;
    log('FechamentosModal', 'loadFechamento', 'Fechamento loaded', result);
  } catch (error) {
    log('FechamentosModal', 'loadFechamento', 'Error loading fechamento', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar fechamento'));
  } finally {
    isLoading.value = false;
  }
};

const closeModal = (): void => {
  log('FechamentosModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  currentMode.value = 'create';
  currentFechamento.value = null;
  currentId.value = null;
  isDeleting.value = false;
  closeResumo.value = null;
};

const handleSave = async (): Promise<void> => {
  log('FechamentosModal', 'handleSave', 'Save triggered', { mode: currentMode.value });

  if (currentMode.value === 'create' || currentMode.value === 'edit') {
    await formRef.value?.saveFechamento();
  } else if (currentMode.value === 'delete') {
    await confirmDelete();
  } else if (currentMode.value === 'reopen') {
    await confirmReopen();
  } else if (currentMode.value === 'close') {
    await confirmClose();
  }
};

const handleSaveSuccess = (savedFechamento?: FechamentoData): void => {
  log('FechamentosModal', 'handleSaveSuccess', 'Save success', savedFechamento);
  const wasCreate = currentMode.value === 'create';
  const savedId = savedFechamento?.id;
  closeModal();
  eventBus.emit('fechamento.saved');
  if (wasCreate && savedId) {
    eventBus.emit('modal.fechamentos.open', { id: savedId, mode: 'view' });
  }
};

const handleEditFromView = (fechamento: FechamentoData): void => {
  log('FechamentosModal', 'handleEditFromView', 'Switching to edit mode', fechamento);
  currentMode.value = 'edit';
  currentFechamento.value = fechamento;
};

const confirmDelete = async (): Promise<void> => {
  if (!currentId.value) {
    log('FechamentosModal', 'confirmDelete', 'No fechamento ID to delete');
    return;
  }

  log('FechamentosModal', 'confirmDelete', 'Deleting fechamento', { id: currentId.value });
  isDeleting.value = true;

  try {
    await deleteFechamento(currentId.value);
    log('FechamentosModal', 'confirmDelete', 'Fechamento deleted', { id: currentId.value });
    eventBus.emit('globalMessage.success', t('Fechamento excluido com sucesso'));
    eventBus.emit('fechamento.deleted', currentId.value);
    eventBus.emit('fechamento.saved');
    closeModal();
  } catch (error) {
    log('FechamentosModal', 'confirmDelete', 'Error deleting fechamento', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', message || t('Erro ao excluir fechamento'));
  } finally {
    isDeleting.value = false;
  }
};

const confirmReopen = async (): Promise<void> => {
  if (!currentId.value || !reopenMotivo.value.trim()) {
    log('FechamentosModal', 'confirmReopen', 'Missing ID or motivo');
    return;
  }

  log('FechamentosModal', 'confirmReopen', 'Reopening fechamento', { id: currentId.value, motivo: reopenMotivo.value });
  isDeleting.value = true;

  try {
    await reabrirFechamento(Number(currentId.value), reopenMotivo.value);
    log('FechamentosModal', 'confirmReopen', 'Fechamento reopened', { id: currentId.value });
    eventBus.emit('globalMessage.success', t('Período reaberto com sucesso'));
    eventBus.emit('fechamento.reopened', currentId.value);
    eventBus.emit('fechamento.saved');
    closeModal();
  } catch (error) {
    log('FechamentosModal', 'confirmReopen', 'Error reopening fechamento', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', message || t('Erro ao reabrir período'));
  } finally {
    isDeleting.value = false;
  }
};

const confirmClose = async (): Promise<void> => {
  if (!currentId.value) {
    log('FechamentosModal', 'confirmClose', 'No fechamento ID to close');
    return;
  }

  log('FechamentosModal', 'confirmClose', 'Closing fechamento', { id: currentId.value });
  isDeleting.value = true;

  try {
    await fecharFechamento(currentId.value);
    log('FechamentosModal', 'confirmClose', 'Fechamento closed', { id: currentId.value });
    eventBus.emit('globalMessage.success', t('Período fechado com sucesso'));
    eventBus.emit('fechamento.closed', currentId.value);
    eventBus.emit('fechamento.saved');
    closeModal();
  } catch (error) {
    log('FechamentosModal', 'confirmClose', 'Error closing fechamento', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', message || t('Erro ao fechar período'));
  } finally {
    isDeleting.value = false;
  }
};

// Lifecycle - eventBus listeners
onMounted(() => {
  log('FechamentosModal', 'onMounted', 'Component mounted, setting up event listeners');
  eventBus.on('modal.fechamentos.open', handleModalOpen);
  eventBus.on('modal.fechamentos.close', closeModal);
});

onBeforeUnmount(() => {
  log('FechamentosModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');
  eventBus.off('modal.fechamentos.open', handleModalOpen);
  eventBus.off('modal.fechamentos.close', closeModal);
});

// Expose public methods
defineExpose({
  closeModal
});
</script>

<style scoped>
/* Modal-specific styles - centralized modal pattern */
.close-summary {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-label {
  color: #6b7280;
  font-size: 0.875rem;
}

.summary-value {
  font-weight: 600;
}
</style>
